import { gql } from '@apollo/client'

export const PROVIDER_ONBOARDING_QUERY = gql`
  query ProviderOnboardingData {
    myProviderProfile {
      id
      bio
      yearsExperience
      languages
      hourlyRateCents
      user {
        id
        avatarUrl
      }
      serviceOfferings {
        id
        customPriceCents
        customDurationMinutes
        serviceType {
          id
        }
      }
      availabilitySlots {
        id
        dayOfWeek
        startTime
        endTime
      }
      serviceAreas {
        id
        postalCode
        areaName
        city
      }
    }
    serviceCategories {
      id
      name
      nameEl
      translationKey
      description
      descriptionEl
      icon
      position
      serviceTypes {
        id
        name
        nameEl
        translationKey
        description
        descriptionEl
        basePriceCents
        estimatedDurationMinutes
      }
    }
  }
`

export const UPDATE_PROFILE_MUTATION = gql`
  mutation UpdateProviderAvatar($avatarUrl: String) {
    updateProfile(input: { avatarUrl: $avatarUrl }) {
      user {
        id
        avatarUrl
      }
      errors
    }
  }
`

export const UPDATE_PROVIDER_PROFILE_MUTATION = gql`
  mutation UpdateProviderBasics(
    $bio: String
    $yearsExperience: Int
    $languages: [String!]
    $hourlyRateCents: Int
  ) {
    updateProviderProfile(input: {
      bio: $bio
      yearsExperience: $yearsExperience
      languages: $languages
      hourlyRateCents: $hourlyRateCents
    }) {
      providerProfile {
        id
        bio
        yearsExperience
        languages
        hourlyRateCents
      }
      errors
    }
  }
`

export const SET_PROVIDER_SERVICES_MUTATION = gql`
  mutation SaveProviderServices($services: [ServiceInput!]!) {
    setProviderServices(input: { services: $services }) {
      providerProfile {
        id
        serviceOfferings {
          id
          customPriceCents
          customDurationMinutes
          serviceType {
            id
          }
        }
      }
      errors
    }
  }
`

export const SET_WEEKLY_AVAILABILITY_MUTATION = gql`
  mutation SaveWeeklyAvailability($slots: [AvailabilitySlotInput!]!) {
    setWeeklyAvailability(input: { slots: $slots }) {
      slots {
        id
        dayOfWeek
        startTime
        endTime
      }
      errors
    }
  }
`

export const SET_SERVICE_AREAS_MUTATION = gql`
  mutation SaveServiceAreas($areas: [AreaInput!]!) {
    setServiceAreas(input: { areas: $areas }) {
      serviceAreas {
        id
        postalCode
        areaName
        city
      }
      errors
    }
  }
`

export interface OnboardingServiceType {
  id: string
  name: string
  nameEl: string
  translationKey: string
  description: string | null
  descriptionEl: string | null
  basePriceCents: number
  estimatedDurationMinutes: number
}

export interface OnboardingServiceCategory {
  id: string
  name: string
  nameEl: string
  translationKey: string
  description: string | null
  descriptionEl: string | null
  icon: string | null
  position: number
  serviceTypes: OnboardingServiceType[]
}

export interface OnboardingProviderProfile {
  id: string
  bio: string | null
  yearsExperience: number | null
  languages: string[]
  hourlyRateCents: number | null
  user: {
    id: string
    avatarUrl: string | null
  }
  serviceOfferings: Array<{
    id: string
    customPriceCents: number | null
    customDurationMinutes: number | null
    serviceType: {
      id: string
    }
  }>
  availabilitySlots: Array<{
    id: string
    dayOfWeek: number
    startTime: string
    endTime: string
  }>
  serviceAreas: Array<{
    id: string
    postalCode: string
    areaName: string | null
    city: string
  }>
}

export interface ProviderOnboardingData {
  myProviderProfile: OnboardingProviderProfile | null
  serviceCategories: OnboardingServiceCategory[]
}

export interface UpdateProfileData {
  updateProfile: {
    user: { id: string; avatarUrl: string | null } | null
    errors: string[]
  }
}

export interface UpdateProviderProfileData {
  updateProviderProfile: {
    providerProfile: {
      id: string
      bio: string | null
      yearsExperience: number | null
      languages: string[]
      hourlyRateCents: number | null
    } | null
    errors: string[]
  }
}

export interface ProviderServiceInput {
  serviceTypeId: string
  customPriceCents?: number
  customDurationMinutes?: number
}

export interface SetProviderServicesData {
  setProviderServices: {
    providerProfile: {
      id: string
      serviceOfferings: OnboardingProviderProfile['serviceOfferings']
    } | null
    errors: string[]
  }
}

export interface AvailabilitySlotInput {
  dayOfWeek: number
  startTime: string
  endTime: string
}

export interface SetWeeklyAvailabilityData {
  setWeeklyAvailability: {
    slots: OnboardingProviderProfile['availabilitySlots']
    errors: string[]
  }
}

export interface ServiceAreaInput {
  postalCode: string
  areaName?: string
  city?: string
}

export interface SetServiceAreasData {
  setServiceAreas: {
    serviceAreas: OnboardingProviderProfile['serviceAreas'] | null
    errors: string[]
  }
}
